import { darken, fade, getContrastText, lighten } from "../lib/colorManipulator";
import type { RAliasArticle, RAliasTheme } from "../routes";
import {
  blackText,
  colorRanges,
  gemRed,
  gemTeal,
  grayscalePalette,
  lightText,
  q01Palette,
  q02Palette,
  q04Palette,
  q06Palette,
  q07Palette,
  q08Palette,
  q09Palette,
  unescoDarkBlue,
  unescoMarineBlue,
  whiteText,
} from "./colors";

const imageAccess = new URL("../assets/bg_access-revision.svg", import.meta.url).href;
const imageEquity = new URL("../assets/bg_equity-revision.svg", import.meta.url).href;
const imageFinanceAid = new URL("../assets/bg_finance_aid-revision.svg", import.meta.url).href;
const imageFinance = new URL("../assets/bg_finance-revision.svg", import.meta.url).href;
const imageLearning = new URL("../assets/bg_learing-revision.svg", import.meta.url).href;
const imageQuality = new URL("../assets/bg_quality-revision.svg", import.meta.url).href;
const imageTrajectories = new URL("../assets/bg_trajectories-revision.svg", import.meta.url).href;

export const zIndex = {
  aside: 1,
  tooltip: 10,
  overlapContext: 11,
  header: 20,
};

/**
 * Static color palette — computed once from the constants in `./colors`, never varies at
 * runtime. Import this directly when you only need colors and not the per-article theme
 * (`palette.theme`, route-dependent) or other client/viewport-derived theme fields.
 */
export const palette = {
  action: {
    main: unescoMarineBlue,
    light: lighten(unescoMarineBlue, 0.3),
    dark: darken(unescoMarineBlue, 0.3),
  },
  text: {
    black: blackText,
    white: whiteText,
    light: lightText,
  },
  brand: {
    blue: {
      main: unescoDarkBlue,
      light: lighten(unescoDarkBlue, 0.3),
      dark: darken(unescoDarkBlue, 0.3),
    },
    marine: {
      main: unescoMarineBlue,
      light: lighten(unescoMarineBlue, 0.3),
      dark: darken(unescoMarineBlue, 0.3),
    },
    teal: {
      main: gemTeal,
      light: lighten(gemTeal, 0.3),
      dark: darken(gemTeal, 0.3),
    },
    red: {
      main: gemRed,
      light: lighten(gemRed, 0.3),
      dark: darken(gemRed, 0.3),
    },
  },
  grayscale: grayscalePalette,
  ranges: colorRanges,
};

/**
 * Static, pure formatting/color-manipulation helpers — never varies at runtime.
 * Import this directly instead of pulling it out of the theme.
 */
export const utils = {
  lighten,
  darken,
  fade,
  getContrastText,
};

export interface DocumentBase {
  textColor: string;
  background: {
    main: string;
    light: string;
    dark: string;
  };
  palette: Array<string>;
}

export interface DocumentDefault extends DocumentBase {
  kind: "default";
}

export interface DocumentArticle extends DocumentBase {
  kind: "article";
  illustration: string;
}

export type DocumentKey = RAliasTheme | RAliasArticle | "default";

export type DocumentTheme = {
  [K in DocumentKey]: DocumentArticle | DocumentDefault;
};

export const themes: DocumentTheme = {
  default: {
    kind: "default",
    textColor: blackText,
    background: {
      main: q01Palette[2],
      light: lighten(q01Palette[2], 0.85),
      dark: darken(q01Palette[2], 0.6),
    },
    palette: q07Palette,
  },
  access: {
    kind: "article",
    textColor: q02Palette[6],
    background: {
      main: q02Palette[3],
      light: lighten(q02Palette[2], 0.85),
      dark: q02Palette[6],
    },
    palette: q02Palette,
    illustration: imageAccess,
  },
  equity: {
    kind: "article",
    textColor: q04Palette[7],
    background: {
      main: q04Palette[7],
      light: lighten(q04Palette[7], 0.85),
      dark: darken(q04Palette[7], 0.6),
    },
    palette: q04Palette,
    illustration: imageEquity,
  },
  learning: {
    kind: "article",
    textColor: q06Palette[6],
    background: {
      main: q06Palette[7],
      light: lighten(q06Palette[7], 0.85),
      dark: darken(q06Palette[7], 0.6),
    },
    palette: q06Palette,
    illustration: imageLearning,
  },
  trajectories: {
    kind: "article",
    textColor: q06Palette[6],
    background: {
      main: q06Palette[7],
      light: lighten(q06Palette[7], 0.85),
      dark: darken(q06Palette[7], 0.6),
    },
    palette: q06Palette,
    illustration: imageTrajectories,
  },
  quality: {
    kind: "article",
    textColor: q08Palette[6],
    background: {
      main: q08Palette[5],
      light: lighten(q08Palette[5], 0.85),
      dark: darken(q08Palette[5], 0.6),
    },
    palette: q08Palette,
    illustration: imageQuality,
  },
  finance: {
    kind: "article",
    textColor: q09Palette[7],
    background: {
      main: q09Palette[4],
      light: lighten(q09Palette[4], 0.85),
      dark: darken(q09Palette[4], 0.6),
    },
    palette: q09Palette,
    illustration: imageFinance,
  },
  "finance-aid": {
    kind: "article",
    textColor: q09Palette[7],
    background: {
      main: q09Palette[4],
      light: lighten(q09Palette[4], 0.85),
      dark: darken(q09Palette[4], 0.6),
    },
    palette: q09Palette,
    illustration: imageFinanceAid,
  },
};
